<template src="./FeedbackQuestions.html"></template>

<script>
import {
  isAnswered,
  answeredCount,
  isSubjectComplete,
  formatAnswer,
} from "../utils/responses";
import { formatDivision, initialsOf } from "../utils/faculty";

export default {
  name: "FeedbackQuestions",

  props: {
    isCourseFeedback: { type: Boolean, default: false },
    subjects: { type: Array, default: () => [] },
    questions: { type: Array, default: () => [] },
    activeSubjectIndex: { type: Number, default: 0 },
  },

  data: () => ({
    activeIndex: 0,
  }),

  computed: {
    activeSubject() {
      return this.isCourseFeedback ? this.subjects[this.activeSubjectIndex] : null;
    },

    activeQuestions() {
      return this.activeSubject ? this.activeSubject.questions : this.questions;
    },

    total() {
      return this.isCourseFeedback
        ? this.subjects.reduce((sum, s) => sum + s.questions.length, 0)
        : this.questions.length;
    },

    answered() {
      return this.isCourseFeedback
        ? this.subjects.reduce((sum, s) => sum + answeredCount(s.questions), 0)
        : answeredCount(this.questions);
    },

    subjectCos() {
      const cos = this.activeQuestions.reduce(
        (all, question) => all.concat(this.questionCos(question)),
        []
      );
      return [...new Set(cos)];
    },

    // Every faculty mapped to the active subject, one entry each, so the
    // division (dropped by the old comma-joined line) stays visible.
    facultyList() {
      return (this.activeSubject?.facultyDetails || [])
        .filter((f) => f.name)
        .map((f) => ({
          name: f.name,
          division: formatDivision(f.division),
          initials: initialsOf(f.name),
        }));
    },

    questionCountLabel() {
      return `${this.activeQuestions.length} questions`;
    },

    isLastSubject() {
      return (
        !this.isCourseFeedback ||
        this.activeSubjectIndex === this.subjects.length - 1
      );
    },

    nextLabel() {
      return this.isLastSubject ? "Continue" : "Next subject";
    },

    canGoPrevious() {
      return this.activeIndex > 0 || this.activeSubjectIndex > 0;
    },
  },

  watch: {
    activeSubjectIndex() {
      this.activeIndex = this.firstUnansweredIndex();
    },
  },

  created() {
    this.activeIndex = this.firstUnansweredIndex();
  },

  methods: {
    isAnswered,
    formatAnswer,

    isComplete(subject) {
      return isSubjectComplete(subject);
    },

    questionCos(question) {
      return question.cos || [];
    },

    firstUnansweredIndex() {
      const index = this.activeQuestions.findIndex((q) => !isAnswered(q));
      return index === -1 ? Math.max(this.activeQuestions.length - 1, 0) : index;
    },

    openQuestion(index) {
      this.activeIndex = index;
    },

    setAnswer(question, value) {
      question.selectedAnswer = value;
      question.errorMessage = "";
      this.advance();
    },

    toggleAnswer(question, value) {
      const index = question.selectedAnswer.indexOf(value);
      if (index === -1) question.selectedAnswer.push(value);
      else question.selectedAnswer.splice(index, 1);
      question.errorMessage = "";
    },

    advance() {
      const next = this.activeQuestions.findIndex(
        (q, i) => i > this.activeIndex && !isAnswered(q)
      );
      if (next !== -1) this.activeIndex = next;
    },

    validate() {
      let valid = true;
      this.activeQuestions.forEach((question) => {
        if (isAnswered(question)) {
          question.errorMessage = "";
        } else {
          question.errorMessage = "This is a required question.";
          valid = false;
        }
      });
      if (!valid) this.activeIndex = this.firstUnansweredIndex();
      return valid;
    },

    selectSubject(index) {
      this.$emit("select-subject", index);
    },

    goPrevious() {
      if (this.activeIndex > 0) {
        this.activeIndex -= 1;
      } else if (this.activeSubjectIndex > 0) {
        this.$emit("select-subject", this.activeSubjectIndex - 1);
      }
    },

    goNext() {
      if (!this.validate()) return;
      if (this.isLastSubject) this.$emit("continue");
      else this.$emit("select-subject", this.activeSubjectIndex + 1);
    },

    cardClass(question, index) {
      if (index === this.activeIndex) return "questions__card--active";
      if (isAnswered(question)) return "questions__card--answered";
      return "questions__card--pending";
    },

    tabClass(subject, index) {
      return {
        "questions__tab--active": index === this.activeSubjectIndex,
        "questions__tab--complete": this.isComplete(subject),
      };
    },

    dotClass(question, index) {
      if (index === this.activeIndex) return "questions__dot--active";
      if (isAnswered(question)) return "questions__dot--answered";
      return "";
    },
  },
};
</script>

<style src="./FeedbackQuestions.css" scoped></style>
